import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, MessageSquare, TreeDeciduous } from 'lucide-react';
import { RIESGO_LABEL, RIESGO_PUNTO, rutaArbol } from 'lib/arboles';
import { plural } from 'lib/auditoria';
import { urlArchivo } from 'services/arbolesService';

// Estado discreto: un punto de color con el nivel en texto. Alto y extremo
// suben a texto de error, sin fondo ni borde: se nota, pero no grita.
export function PuntoEstado({ a }) {
  if (a.activo === false) {
    return <span className="text-xs font-medium text-muted-foreground">Dado de baja</span>;
  }
  if (!a.riesgo) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <span className="h-2 w-2 rounded-full border border-muted-foreground/60" aria-hidden="true" />Sin evaluar
      </span>
    );
  }
  const fuerte = a.riesgo === 'alto' || a.riesgo === 'extremo';
  return (
    <span data-riesgo={a.riesgo}
      className={`inline-flex items-center gap-1.5 text-xs font-medium ${fuerte ? 'text-error' : 'text-muted-foreground'}`}>
      <span className={`h-2 w-2 rounded-full ${RIESGO_PUNTO[a.riesgo] || 'bg-muted-foreground'}`} aria-hidden="true" />
      <span className="sr-only">Riesgo </span>{RIESGO_LABEL[a.riesgo] || a.riesgo}
    </span>
  );
}

function Miniatura({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let vivo = true;
    setUrl(null);
    if (path) urlArchivo(path).then((u) => { if (vivo) setUrl(u || null); }).catch(() => {});
    return () => { vivo = false; };
  }, [path]);
  if (!url) return null;
  return <img src={url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover ring-1 ring-black/5" loading="lazy" />;
}

const etiquetaAria = (a) => {
  const partes = [`Árbol ${a.codigo}`];
  if (a.nombre_comun || a.especie) partes.push(a.nombre_comun || a.especie);
  if (a.sostiene) partes.push(`sostiene ${a.sostiene}`);
  if (a.activo === false) partes.push('dado de baja');
  else partes.push(a.riesgo ? `riesgo ${String(RIESGO_LABEL[a.riesgo] || a.riesgo).toLowerCase()}` : 'sin evaluar');
  if (a.vencida && a.activo !== false) partes.push('revisión pendiente');
  if (a.menciones_30d > 0 && a.activo !== false) partes.push(`${plural(a.menciones_30d, 'mención', 'menciones')} en la inspección diaria (30 días)`);
  return partes.join(', ');
};

// Ficha resumida de un árbol en el tablero: toda la tarjeta abre su pantalla.
export default function TarjetaArbol({ a }) {
  const nombre = a.nombre_comun || a.especie || 'Especie sin registrar';
  const cientifico = a.nombre_comun && a.especie ? a.especie : null;
  const baja = a.activo === false;
  return (
    <li data-testid="arbol" className="list-none">
      <Link to={rutaArbol(a.id)} aria-label={etiquetaAria(a)}
        className={`group relative flex h-full min-h-[148px] flex-col overflow-hidden rounded-xl border border-border bg-card p-3.5 text-card-foreground
          transition-[border-color,box-shadow,transform] duration-150 ease-out
          [@media(hover:hover)]:hover:border-primary/35 [@media(hover:hover)]:hover:shadow-sm active:scale-[0.985] motion-reduce:transition-none motion-reduce:active:scale-100
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
          sm:p-4 ${baja ? 'opacity-60' : ''}`}>
        <TreeDeciduous size={84} strokeWidth={1.1} aria-hidden="true"
          className="pointer-events-none absolute -bottom-4 -right-4 text-primary/[0.06] transition-colors [@media(hover:hover)]:group-hover:text-primary/10" />

        <span className="flex items-start justify-between gap-2">
          <span className="font-heading text-[26px] font-bold leading-none tracking-tight text-foreground tabular-nums sm:text-[28px]">
            {a.codigo}
          </span>
          {a.foto_path ? <Miniatura path={a.foto_path} /> : <span className="pt-1"><PuntoEstado a={a} /></span>}
        </span>

        <span className="mt-3 block min-w-0">
          <span className="block truncate text-[15px] font-semibold leading-snug text-foreground">{nombre}</span>
          {cientifico && <span className="block truncate text-xs italic text-muted-foreground">{cientifico}</span>}
        </span>

        <span className="relative mt-auto block space-y-1 pt-3 text-xs text-muted-foreground">
          {a.foto_path && <span className="block"><PuntoEstado a={a} /></span>}
          <span className="block truncate">{a.sostiene || 'Sin elemento registrado'}</span>
          {a.vencida && !baja && (
            <span className="flex items-center gap-1">
              <CalendarClock size={12} aria-hidden="true" className="shrink-0" />Revisión pendiente
            </span>
          )}
          {a.menciones_30d > 0 && !baja && (
            <span className="flex items-center gap-1" title="En la inspección diaria de los últimos 30 días">
              <MessageSquare size={12} aria-hidden="true" className="shrink-0" />{plural(a.menciones_30d, 'mención', 'menciones')}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}

// Esqueleto de carga con la misma silueta que la tarjeta.
export function TarjetaEsqueleto() {
  return (
    <li className="list-none rounded-xl border border-border bg-card p-3.5 sm:p-4" aria-hidden="true">
      <span className="flex items-start justify-between">
        <span className="h-6 w-10 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
        <span className="h-3 w-12 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
      </span>
      <span className="mt-4 block h-4 w-24 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
      <span className="mt-1.5 block h-3 w-16 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
      <span className="mt-8 block h-3 w-20 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
    </li>
  );
}
